/**
 * Versioned snapshot of a MolecularWorkspace.
 *
 * Index references (a1, a2, src, dst, etc.) are self-referential — they point
 * to positions within THIS snapshot's arrays, not to any canonical ordering.
 * Two snapshots of the same workspace may have different array orderings.
 * Use graph-based comparison (workspace-compare) for equality, never string compare.
 */
export interface WorkspaceSnapshotV1 {
  v: 1;
  atoms: SnapshotAtom[];
  bonds: SnapshotBond[];
  arrows?: SnapshotArrow[];
  hydrogenBonds?: SnapshotHydrogenBond[];
  visibility?: Record<number, 'h' | 'd'>;
  annotations?: SnapshotAnnotation[];
  groups?: SnapshotGroup[];
  templates?: SnapshotTemplate[];
  instances?: SnapshotGroupInstance[];
}

export interface SnapshotAtom {
  symbol: string;
  x: number;
  y: number;
  charge?: number;
  lonePairs?: number;
  radicals?: number;
  /** R/S chirality on a stereocenter (PIPH-269). */
  chirality?: 'r' | 's';
}

export interface SnapshotBond {
  a1: number;
  a2: number;
  order: number;
  type?: 'l' | 'd' | 'w' | 's';
  /** E/Z geometry on an alkene (PIPH-269). */
  isomerConfig?: 'e' | 'z';
}

export interface SnapshotArrow {
  type: string;
  src: number;
  dst: number;
  anchor?: number;
}

export interface SnapshotHydrogenBond {
  donor: number;
  donorParent: number;
  acceptor: number;
}

/**
 * Snapshot of a single annotation (symbol or text).
 * Free-floating — no atom/bond index references.
 * Optional fields omitted when at default values (rotation=0, scale=1).
 */
export interface SnapshotAnnotation {
  type: 'symbol' | 'text';
  x: number;
  y: number;
  rotation?: number;        // Omitted if 0
  scale?: number;           // Omitted if 1
  // Symbol-specific
  symbolId?: string;        // For type: 'symbol'
  shaftLength?: number;     // For type: 'symbol' — non-uniform shaft scaling
  // Text-specific
  text?: string;            // For type: 'text'
  fontSize?: number;        // For type: 'text'
  alignment?: 'left' | 'center' | 'right';
}

/**
 * Snapshot of a group (named collection of atoms).
 * Atom membership stored as indices into the snapshot's atoms array.
 */
export interface SnapshotGroup {
  name: string;
  templateId?: string;
  atoms: number[];              // Indices into SnapshotAtom[]
}

/**
 * Snapshot of a template (master molecular structure).
 * Self-contained: atoms, bonds, and visibility inline.
 */
export interface SnapshotTemplate {
  id: string;
  name: string;
  category?: string;
  atoms: SnapshotAtom[];
  bonds: SnapshotBond[];
  visibility?: Record<number, 'h' | 'd'>;
}

/**
 * Snapshot of a group instance (placed template with transform).
 * References a template by ID. Override maps use local atom indices
 * into the referenced template's atoms array.
 */
export interface SnapshotGroupInstance {
  templateId: string;
  tx: number;
  ty: number;
  rotation: number;
  overrides?: SnapshotInstanceOverrides;
}

export interface SnapshotInstanceOverrides {
  visibility?: Record<number, 'h' | 'd'>;
  elements?: Record<number, string>;
  charges?: Record<number, number>;
  lonePairs?: Record<number, number>;
  radicals?: Record<number, number>;
}

/** Branded type for opaque serialized workspace strings. */
export type OpaqueWorkspaceString = string & { readonly __brand: unique symbol };

export const OPAQUE_PREFIX = 'alchemie:ws:v1:';
